import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Plus, ArrowLeft, Pencil, Trash2, RefreshCw, X, UsersRound } from 'lucide-react';
import {
  fetchTeamMembers,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  IMG_BASE_URL,
} from './hooks/hooks';

const NAV  = '#001f3f';
const TEAL = '#0d9488';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  position: Yup.string().required('Position is required'),
  experience_years: Yup.number().min(0).required('Required'),
  years_in_position: Yup.number().min(0).required('Required'),
});

const Toast = ({ toast, onClose }) => {
  if (!toast.open) return null;
  const cls = toast.severity === 'success'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`${cls} border rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 max-w-sm`}>
        <span className="text-sm font-medium">{toast.message}</span>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

function TeamManagement() {
  const queryClient = useQueryClient();
  const { data: members, error, isLoading, refetch } = useQuery({
    queryKey: ['team-members'],
    queryFn: fetchTeamMembers,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const deleteMutation = useMutation({
    mutationFn: deleteTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setToast({ open: true, message: 'Team member removed.', severity: 'success' });
      refetch();
    },
    onError: () => setToast({ open: true, message: 'Failed to remove team member.', severity: 'error' }),
  });

  const saveMutation = useMutation({
    mutationFn: (values) => (editing ? updateTeamMember({ id: editing.id, data: values }) : createTeamMember(values)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setOpen(false);
      setEditing(null);
      setToast({ open: true, message: editing ? 'Team member updated.' : 'Team member added.', severity: 'success' });
      refetch();
    },
    onError: () => setToast({ open: true, message: 'Failed to save team member. Try again.', severity: 'error' }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name || '',
      position: editing?.position || '',
      bio: editing?.bio || '',
      experience_years: editing?.experience_years ?? 0,
      years_in_position: editing?.years_in_position ?? 0,
      is_leadership: editing?.is_leadership || false,
      order: editing?.order ?? 0,
      photo: null,
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (key === 'photo' && !value) return;
        formData.append(key, value);
      });
      saveMutation.mutate(formData);
    },
  });

  const startAdd = () => { setEditing(null); formik.resetForm(); setOpen(true); };
  const startEdit = (member) => { setEditing(member); setOpen(true); };
  const cancel = () => { setOpen(false); setEditing(null); };

  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2';

  // Plain JSX, not a nested component: a component declared here would be a
  // new type on every render, remounting the inputs and dropping focus after
  // a single character.
  const form = (
    <div className="max-w-2xl mx-auto">
      <button onClick={cancel} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 mb-4 transition-colors">
        <ArrowLeft size={16} />
        <span className="font-medium">Back to Team List</span>
      </button>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
          <UsersRound size={16} style={{ color: NAV }} />
          <div>
            <h3 className="text-sm font-bold text-slate-800">{editing ? 'Edit Team Member' : 'Add Team Member'}</h3>
            <p className="text-xs text-slate-400">Shown on the corporate website's Team page.</p>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Full name</label>
              <input name="name" value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur} className={inputCls} />
              {formik.touched.name && formik.errors.name && <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>}
            </div>
            <div>
              <label className={labelCls}>Position</label>
              <input name="position" value={formik.values.position} onChange={formik.handleChange} onBlur={formik.handleBlur} className={inputCls} />
              {formik.touched.position && formik.errors.position && <p className="text-red-500 text-xs mt-1">{formik.errors.position}</p>}
            </div>
            <div>
              <label className={labelCls}>Years of experience</label>
              <input type="number" min="0" name="experience_years" value={formik.values.experience_years} onChange={formik.handleChange} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Years in this position</label>
              <input type="number" min="0" name="years_in_position" value={formik.values.years_in_position} onChange={formik.handleChange} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Bio (used for leadership profile)</label>
            <textarea name="bio" rows={3} value={formik.values.bio} onChange={formik.handleChange} className={inputCls} />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <input type="checkbox" name="is_leadership" checked={formik.values.is_leadership} onChange={formik.handleChange} />
              Feature as leadership
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Order</label>
              <input type="number" name="order" value={formik.values.order} onChange={formik.handleChange} className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Photo</label>
            <input type="file" accept="image/*" onChange={(e) => formik.setFieldValue('photo', e.target.files[0])} className="text-sm text-slate-600" />
            {editing?.photo && !formik.values.photo && (
              <img src={`${IMG_BASE_URL}${editing.photo}`} alt={editing.name} className="mt-3 w-20 h-20 object-cover rounded-lg border border-slate-200" />
            )}
          </div>

          <button type="submit" disabled={saveMutation.isLoading}
            className="w-full bg-teal-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors disabled:opacity-50">
            {saveMutation.isLoading ? 'Saving...' : editing ? 'Save Changes' : 'Add Team Member'}
          </button>
        </form>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
          <h3 className="text-sm font-bold text-red-800 mb-1">Error Loading Team</h3>
          <p className="text-sm text-red-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {open ? form : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Team Management</h2>
              <p className="text-xs text-slate-400 mt-0.5">Manage the people shown on the website's Team page.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                <RefreshCw size={13} /> Refresh
              </button>
              <button onClick={startAdd} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
                <Plus size={13} /> Add Team Member
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {members?.map((m) => (
                <div key={m.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
                  <div className="p-5 flex gap-4">
                    {m.photo ? (
                      <img src={`${IMG_BASE_URL}${m.photo}`} alt={m.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                           style={{ backgroundColor: TEAL + '18', color: TEAL }}>
                        {m.name?.charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{m.name}</p>
                      <p className="text-sm text-slate-500 truncate">{m.position}</p>
                      {m.is_leadership && (
                        <span className="inline-block mt-1 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                          Leadership
                        </span>
                      )}
                      <p className="text-xs text-slate-400 mt-1">{m.experience_years}y experience · {m.years_in_position}y in role</p>
                    </div>
                  </div>
                  <div className="flex border-t border-slate-100">
                    <button onClick={() => startEdit(m)} className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-slate-600 hover:bg-slate-50">
                      <Pencil size={14} /> Edit
                    </button>
                    <button onClick={() => deleteMutation.mutate(m.id)} disabled={deleteMutation.isLoading}
                      className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-red-600 hover:bg-red-50 border-l border-slate-100">
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
              {members?.length === 0 && (
                <div className="col-span-full text-center py-10 text-slate-400">No team members yet</div>
              )}
            </div>
          )}
        </>
      )}
      <Toast toast={toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
    </div>
  );
}

export default TeamManagement;
